<script setup lang="ts" generic="T extends { id: string }">
import type { PropType, StyleValue } from 'vue';

import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
  items: {
    type: Array as PropType<Array<T>>,
    required: true,
  },
  minItemWidth: {
    type: Number,
    required: true,
  },
  maxItemWidth: {
    type: Number,
    required: true,
  },
  gap: {
    type: Number,
    default: 20,
  },
  style: {
    type: [Object, String] as PropType<StyleValue>,
    required: false,
  },
});

const $root = ref<HTMLElement>();
const page = ref(1);
const pageWidth = ref(0);
const itemPerPage = ref(1);
const resizeObserver = ref<ResizeObserver>();
const touchPositionOffset = ref(0);
const movingWithTouch = ref(false);

const touchPositionUpdateDelayMs = 5;
let touchPositionXStart = 0;
let touchPositionOffsetTmp = 0;
let lastUpdate = new Date().getTime();
let touchPositionUpdateDebounce = -1;

const canGoPreviousPage = computed(() => {
  return page.value > 1;
});

const pages = computed<Array<Array<T>>>(() => {
  const pages = new Array<Array<T>>();
  let currentPage = new Array<T>();
  props.items.forEach((item) => {
    currentPage.push(item);
    if (currentPage.length >= itemPerPage.value) {
      pages.push(currentPage);
      currentPage = [];
    }
  });
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }
  return pages;
});

const maxPage = computed(() => pages.value.length);

const canGoNextPage = computed(() => {
  return page.value < maxPage.value;
});

const carouselStyle = computed<StyleValue>(() => {
  const style: StyleValue = {};
  style.transform = `translateX(-${(page.value - 1) * pageWidth.value + touchPositionOffset.value}px)`;
  if (movingWithTouch.value) {
    style.transition = 'none';
  }
  return style;
});

function updateRootInformations() {
  const root = $root.value;
  if (root == null) {
    return;
  }
  const rootWidth = root.getBoundingClientRect().width;
  itemPerPage.value = Math.max(1, Math.floor(rootWidth / (props.minItemWidth + 0.80 * props.gap)));
  pageWidth.value = rootWidth;
}

function onTouchStart(event: TouchEvent) {
  touchPositionXStart = event.touches[0].clientX;
  movingWithTouch.value = true;
  window.addEventListener('touchmove', onTouchMove);
  window.addEventListener('touchend', onTouchEnd);
}

function onTouchMove(event: TouchEvent) {
  touchPositionOffsetTmp = 1.15 * (touchPositionXStart - event.touches[0].clientX);
  scheduleTouchPositionUpdate();
}

function scheduleTouchPositionUpdate() {
  const now = new Date().getTime();
  const diff = now - lastUpdate;
  if (diff < touchPositionUpdateDelayMs && touchPositionUpdateDebounce !== -1) {
    return;
  }
  lastUpdate = now;
  window.clearTimeout(touchPositionUpdateDebounce);
  touchPositionUpdateDebounce = window.setTimeout(() => {
    touchPositionOffset.value = touchPositionOffsetTmp;
    touchPositionUpdateDebounce = -1;
  }, touchPositionUpdateDelayMs);
}

function onTouchEnd() {
  window.removeEventListener('touchmove', onTouchMove);
  window.removeEventListener('touchend', onTouchEnd);
  window.clearTimeout(touchPositionUpdateDebounce);
  movingWithTouch.value = false;
  const root = $root.value;
  if (root == null) {
    return;
  }
  const rootWidth = root.getBoundingClientRect().width;
  const pageOffset = Math.round(touchPositionOffset.value / rootWidth);
  let newPage = page.value + pageOffset;
  // If we did not change page we still check if user moved at least 20% it a direction to make it easier to swipe page by page
  if (pageOffset === 0) {
    if (Math.abs(touchPositionOffset.value) >= rootWidth * 0.2) {
      newPage = page.value + Math.sign(touchPositionOffset.value);
    }
  }
  if (newPage >= 0 && newPage <= maxPage.value) {
    page.value = newPage;
  }
  touchPositionOffset.value = 0;
}

onMounted(() => {
  resizeObserver.value = new ResizeObserver(updateRootInformations);
  resizeObserver.value.observe($root.value!);
  window.addEventListener('resize', updateRootInformations);
});

onUnmounted(() => {
  resizeObserver.value?.disconnect();
});

watch(maxPage, () => page.value = 1);
</script>

<template>
  <div ref="$root" class="carousel-controls" :style>
    <div class="carousel-container" :style="{ minWidth: `${pageWidth}px` }" @touchstart.passive="onTouchStart">
      <div class="carousel-body">
        <div
          class="carousel"
          :data-items="items.length"
          :data-page="page"
          :data-item-per-page="itemPerPage"
          :data-max-page="maxPage"
          :style="carouselStyle"
        >
          <div v-for="(page, i) in pages" :key="i" class="carousel-page" :style="{ minWidth: `${pageWidth}px`, gap: `${gap}px` }" :last-page="i === maxPage - 1" :items-per-page="itemPerPage">
            <div v-for="item in page" :key="item.id" class="carousel-item" :style="{ minWidth: `${minItemWidth}px`, maxWidth: `${maxItemWidth}px` }">
              <slot name="item" :item>
                {{ i }}
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="canGoPreviousPage" role="button" tabindex="0" class="arrow" @click="page--" @keydown.enter="page--">
      <ChevronLeft />
    </div>
    <div v-if="canGoNextPage" role="button" tabindex="0" class="arrow right" @click="page++" @keydown.enter="page++">
      <ChevronRight />
    </div>
  </div>
</template>

<style lang="css" scoped>
.carousel-controls {
  display: flex;
  position: relative;
  padding: 0 0;
  min-height: 100%;
}

.carousel-container {
  display: flex;
  overflow: hidden;
  position: relative;
  user-select: none;
  -webkit-user-drag: none;
  flex: 1;
}

.carousel-body {
  position: relative;
  flex: 1;
}

.carousel {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  transition: transform ease-in-out 200ms;
}

.carousel-page {
  display: flex;
  justify-content: flex-start;
}

.carousel-page[max-page='1']:not([items-per-page='1']),
.carousel-page[last-page='true']:not([items-per-page='1']) {
  justify-content: flex-start;
}

.carousel-item {
  height: 99%;
}

.arrow {
  position: absolute;
  top: 50%;
  background: white;
  border-radius: 100%;
  --size: 1.3rem;
  width: var(--size);
  height: var(--size);
  padding: 0.4rem;
  cursor: pointer;
  user-select: none;
  border: 1px solid grey;
  transform: translateY(-50%);

  &:not(.right) {
    left: -16px;
  }

  &.right {
    right: -16px;
  }

  & > * {
    width: 100%;
    height: 100%;
  }
}
</style>
